/**
 * Reno System – Balance Sheet / Monthly Financial Report
 * -----------------------------------------------------------------------
 * Every financial figure on this page — totals, carry forward,
 * reconciliation, mortgage and partner share — is calculated by the backend
 * (App\Services\Finance\MonthlyFinancialReportService) and normalized ONCE
 * by utils/normalizeReport. The screen, the report PDF (utils/pdfReport) and
 * the printed report (components/PrintableReport) all render that same
 * object; nothing here adds up transactions.
 *
 * Report actions (Download Report PDF / Print Report) always produce the
 * full financial report for the selected period. The transaction ledger has
 * its own, separate exports.
 *
 *   GET  /project/{id}/monthly-financial-report?year&month          Monthly view
 *   GET  /project/{id}/balance-sheet?start_date&end_date            This Year / All Time / Custom
 *   GET  /project/{id}/monthly-financial-report/transactions        drill-down
 *   GET  /project/{id}/monthly-financial-report/history?year
 *   PUT  /project/{id}/monthly-financial-report/{year}/{month}      month settings
 *   POST /project/{id}/monthly-financial-report/{year}/{month}/close | /reopen
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Box, Button, Card, Grid, IconButton, LinearProgress, Skeleton, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SummarizeIcon from "@mui/icons-material/Summarize";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useNotifier } from "../../Core/Notifier";
import LogoMain from "../../Images/logo_main_black.webp";
import { apiErrorMessage, closeMonth, fetchHistory, fetchReport, reopenMonth, saveMonthSettings } from "./api";
import { ApiReport, BreakdownRow, DrilldownTarget, HistoryRow, MonthSettingsInput, PeriodSelection, ReportData } from "./types";
import { normalizeHistory, normalizeReport } from "./utils/normalizeReport";
import { COLOR, fmt, getTodayInTZ } from "./utils/format";
import { exportLedgerExcel, exportLedgerPdf } from "./utils/exportLedger";
import { downloadReportPdf } from "./utils/pdfReport";
import { reportTitle } from "./utils/reportSections";
import PeriodToolbar, { RangePreset } from "./components/PeriodToolbar";
import MonthlySummaryCards from "./components/MonthlySummaryCards";
import BreakdownTable from "./components/BreakdownTable";
import TransactionDrilldownDialog from "./components/TransactionDrilldownDialog";
import LedgerTable from "./components/LedgerTable";
import MonthlyHistory from "./components/MonthlyHistory";
import MonthStatusCard from "./components/MonthStatusCard";
import ReportActionBar from "./components/ReportActionBar";
import ReconciliationCard from "./components/ReconciliationCard";
import MonthSettingsCard from "./components/MonthSettingsCard";
import PartnerShareTable from "./components/PartnerShareTable";
import PrintableReport from "./components/PrintableReport";
import { AttachmentViewer } from "./components/AttachmentViewer";

/** Month from ?year=&month=, defaulting to the current month in Toronto. */
const initialPeriod = (params: URLSearchParams): PeriodSelection => {
  const today = getTodayInTZ();
  const year = Number(params.get("year"));
  const month = Number(params.get("month"));
  return year >= 2000 && month >= 1 && month <= 12
    ? { mode: "month", year, month }
    : { mode: "month", year: today.year, month: today.month };
};

/** Is this report the one for the selected period? (Never show one period's figures under another's controls.) */
const reportIsFor = (report: ReportData | null, period: PeriodSelection): report is ReportData => {
  if (!report) return false;
  if (period.mode === "month") {
    return report.period.mode === "month" && report.period.year === period.year && report.period.month === period.month;
  }
  return (
    report.period.mode === "range" &&
    (report.period.startDate ?? "") === (period.startDate ?? "") &&
    (report.period.endDate ?? "") === (period.endDate ?? "")
  );
};

const invalidRange = (period: PeriodSelection) =>
  period.mode === "range" && !!period.startDate && !!period.endDate && period.endDate < period.startDate;

/** Placeholder with the report's shape, so the layout stays put while loading. */
const ReportSkeleton: React.FC = () => (
  <>
    <Grid container spacing={1.5} sx={{ mb: 3 }}>
      {[0, 1, 2, 3].map((i) => (
        <Grid item xs={12} sm={6} md={3} key={i}>
          <Skeleton variant="rounded" height={72} />
        </Grid>
      ))}
    </Grid>
    <Grid container spacing={3} sx={{ mb: 3 }}>
      {[0, 1].map((i) => (
        <Grid item xs={12} md={6} key={i}>
          <Skeleton variant="rounded" height={220} />
        </Grid>
      ))}
    </Grid>
    <Skeleton variant="rounded" height={140} sx={{ mb: 3 }} />
  </>
);

const BalanceSheet: React.FC = () => {
  const { projectId = "" } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showMessage, showErrorMessage: showError } = useNotifier();

  const [period, setPeriod] = useState<PeriodSelection>(() => initialPeriod(searchParams));
  const [rangePreset, setRangePreset] = useState<RangePreset | null>(null);
  const [report, setReport] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [mutating, setMutating] = useState(false);
  const latestRequest = useRef(0);

  const [historyYear, setHistoryYear] = useState(period.mode === "month" ? period.year : getTodayInTZ().year);
  const [history, setHistory] = useState<HistoryRow[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyVersion, setHistoryVersion] = useState(0);
  const [historyBusyKey, setHistoryBusyKey] = useState<string | null>(null);

  const [drilldown, setDrilldown] = useState<(DrilldownTarget & { amount: number }) | null>(null);
  const [files, setFiles] = useState<string[]>([]);
  const [exportingLedgerPdf, setExportingLedgerPdf] = useState(false);

  // Print: the printed report normally mirrors the report on screen; a
  // history row's Print swaps in that month's report until printing ends.
  const [printOverride, setPrintOverride] = useState<ReportData | null>(null);
  const [printRequest, setPrintRequest] = useState(0);

  // --- Data ----------------------------------------------------------------

  useEffect(() => {
    if (invalidRange(period)) {
      setLoadError(null);
      return;
    }
    // Only the newest request may update the page: switching Oct -> Sep
    // quickly must never let a late October response replace September.
    const requestId = ++latestRequest.current;
    setLoading(true);
    setLoadError(null);
    fetchReport(projectId, period)
      .then((api) => {
        if (requestId === latestRequest.current) setReport(normalizeReport(api));
      })
      .catch((error) => {
        if (requestId !== latestRequest.current) return;
        console.error("Failed to load balance sheet", error);
        setLoadError(apiErrorMessage(error, "Failed to load the financial report."));
      })
      .finally(() => {
        if (requestId === latestRequest.current) setLoading(false);
      });
  }, [projectId, period, reloadKey]);

  useEffect(() => {
    let cancelled = false;
    setHistoryLoading(true);
    fetchHistory(projectId, historyYear)
      .then((rows) => !cancelled && setHistory(normalizeHistory(rows)))
      .catch(() => !cancelled && setHistory([]))
      .finally(() => !cancelled && setHistoryLoading(false));
    return () => {
      cancelled = true;
    };
  }, [projectId, historyYear, historyVersion]);

  useEffect(() => {
    if (!printRequest) return;
    const timer = window.setTimeout(() => window.print(), 100); // let the printed copy render first
    return () => window.clearTimeout(timer);
  }, [printRequest]);

  useEffect(() => {
    const clear = () => setPrintOverride(null);
    window.addEventListener("afterprint", clear);
    return () => window.removeEventListener("afterprint", clear);
  }, []);

  // --- Period changes ------------------------------------------------------

  const changePeriod = (next: PeriodSelection, preset: RangePreset | null) => {
    setPeriod(next);
    setRangePreset(preset);
    if (next.mode === "month") {
      setSearchParams({ year: String(next.year), month: String(next.month) }, { replace: true });
      setHistoryYear(next.year);
    } else {
      setSearchParams({}, { replace: true });
    }
  };

  const openMonth = (year: number, month: number) => {
    changePeriod({ mode: "month", year, month }, null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const openDrilldown = (row: BreakdownRow) => setDrilldown({ kind: row.kind, id: row.id, name: row.name, amount: row.amount });

  // Memoized so the drill-down dialog doesn't refetch on unrelated re-renders.
  const drilldownTarget = useMemo<DrilldownTarget | null>(
    () => (drilldown ? { kind: drilldown.kind, id: drilldown.id, name: drilldown.name } : null),
    [drilldown]
  );

  // --- Month actions -------------------------------------------------------

  const runMonthAction = async (action: () => Promise<ApiReport>, success: string, failure: string) => {
    setMutating(true);
    try {
      const api = await action();
      latestRequest.current++; // supersede any in-flight load for the same month
      setReport(normalizeReport(api));
      setLoading(false);
      setHistoryVersion((v) => v + 1);
      showMessage(success);
    } catch (error) {
      showError(apiErrorMessage(error, failure));
    } finally {
      setMutating(false);
    }
  };

  const monthPeriod = period.mode === "month" ? period : null;

  const handleSaveSettings = (settings: MonthSettingsInput) =>
    monthPeriod &&
    runMonthAction(() => saveMonthSettings(projectId, monthPeriod.year, monthPeriod.month, settings), "Month settings saved.", "Failed to save month settings.");

  const handleCloseMonth = () =>
    monthPeriod && runMonthAction(() => closeMonth(projectId, monthPeriod.year, monthPeriod.month), "Month closed.", "Failed to close the month.");

  const handleReopenMonth = () =>
    monthPeriod && runMonthAction(() => reopenMonth(projectId, monthPeriod.year, monthPeriod.month), "Month reopened.", "Failed to reopen the month.");

  // --- Report PDF / print (the full report, never the ledger) -------------

  const handleReportPdf = (target: ReportData) => {
    try {
      downloadReportPdf(target);
    } catch (error) {
      console.error("Failed to export PDF", error);
      showError("Failed to export PDF.");
    }
  };

  const printReport = (target: ReportData | null) => {
    setPrintOverride(target);
    setPrintRequest((n) => n + 1);
  };

  /** History rows fetch THEIR month, so a PDF/print is never built from the month on screen. */
  const withHistoryReport = async (year: number, month: number, use: (r: ReportData) => void) => {
    const key = `${year}-${month}`;
    setHistoryBusyKey(key);
    try {
      use(normalizeReport(await fetchReport(projectId, { mode: "month", year, month })));
    } catch (error) {
      showError(apiErrorMessage(error, "Failed to load that month."));
    } finally {
      setHistoryBusyKey(null);
    }
  };

  // --- Ledger exports (transactions only) ----------------------------------

  const handleLedgerPdf = async (target: ReportData) => {
    setExportingLedgerPdf(true);
    try {
      await exportLedgerPdf(target, target.ledger, LogoMain);
    } catch (error) {
      console.error("Failed to export transactions PDF", error);
      showError("Failed to export transactions PDF.");
    } finally {
      setExportingLedgerPdf(false);
    }
  };

  // --- Render --------------------------------------------------------------

  const isMonth = period.mode === "month";
  const current = reportIsFor(report, period) ? report : null; // the report for what's selected, or null while loading
  const monthly = current?.monthly ?? null;
  const printable = printOverride ?? current;

  return (
    <>
      {/* Header */}
      <Grid container spacing={2} alignItems="center" sx={{ mt: 1, mb: 2 }}>
        <Grid item>
          <IconButton onClick={() => navigate(-1)}>
            <ArrowBackIcon />
          </IconButton>
        </Grid>
        <Grid item xs>
          <Typography variant="h5" fontWeight={700}>
            {current ? reportTitle(current) : isMonth ? "Monthly Financial Report" : "Financial Report"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {current ? `${current.project.name} · ${current.period.label}` : report?.project.name ?? " "}
          </Typography>
        </Grid>
        <Grid item>
          <Button variant="text" startIcon={<SummarizeIcon />} onClick={() => navigate(`/monthly-brief-report/${projectId}`)}>
            Monthly Brief Report
          </Button>
        </Grid>
      </Grid>

      <PeriodToolbar period={period} rangePreset={rangePreset} onChange={changePeriod} />

      {monthly && current && <MonthStatusCard report={current} busy={mutating} onClose={handleCloseMonth} onReopen={handleReopenMonth} />}

      {/* ---- The financial report: always mounted, for every period ---- */}
      <Box component="section" aria-label="Financial report">
        <ReportActionBar
          title={isMonth ? "Monthly Summary" : "Period Summary"}
          disabled={!current}
          onPdf={() => current && handleReportPdf(current)}
          onPrint={() => printReport(null)}
        />
        {loading && <LinearProgress sx={{ mb: 1, borderRadius: 1 }} />}

        {invalidRange(period) && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            The end date is before the start date. Adjust the custom range to see the report.
          </Alert>
        )}
        {loadError && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" size="small" onClick={() => setReloadKey((k) => k + 1)}>
                Retry
              </Button>
            }
          >
            {loadError}
          </Alert>
        )}

        {!current ? (
          <ReportSkeleton />
        ) : (
          <>
            <MonthlySummaryCards report={current} />
            {monthly && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: -2, mb: 2 }}>
                Carry forward: {monthly.carryForwardSource.label}
              </Typography>
            )}

            {current.notCounted.length > 0 && (
              <Alert severity="info" sx={{ mb: 3 }}>
                Not counted in this period (cash basis):{" "}
                {current.notCounted.map((n) => `${n.count} ${n.label}${n.count === 1 ? "" : "s"} (${fmt(n.amount)})`).join(", ")}.
              </Alert>
            )}

            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <BreakdownTable
                  title="Detailed Income"
                  columnLabel="Income Source"
                  sections={[
                    { rows: current.incomeBreakdown },
                    { title: "Partner Deposits", rows: current.depositBreakdown },
                  ]}
                  totalLabel="Total Income"
                  total={current.incomeTotal}
                  headerBg={COLOR.greenBg}
                  totalColor={COLOR.greenDark}
                  emptyLabel="No income in this period."
                  onRowClick={openDrilldown}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <BreakdownTable
                  title="Detailed Expenses"
                  columnLabel="Expense Category"
                  sections={[{ rows: current.expenseBreakdown }]}
                  totalLabel="Total Expenses"
                  total={current.expenseTotal}
                  headerBg={COLOR.redBg}
                  totalColor={COLOR.redDark}
                  emptyLabel="No expenses in this period."
                  onRowClick={openDrilldown}
                />
              </Grid>
            </Grid>

            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} md={monthly ? 5 : 12}>
                <ReconciliationCard report={current} />
              </Grid>
              {monthly && (
                <Grid item xs={12} md={7}>
                  <MonthSettingsCard report={current} saving={mutating} onSave={handleSaveSettings} />
                </Grid>
              )}
            </Grid>

            {/* Monthly only; shows "setup incomplete" instead of disappearing. */}
            <PartnerShareTable report={current} />
          </>
        )}
      </Box>

      {/* ---- Transactions: separate from the report, with their own exports ---- */}
      {current ? (
        <LedgerTable
          report={current}
          onOpenFiles={setFiles}
          onExportExcel={() => exportLedgerExcel(current, current.ledger)}
          onExportPdf={() => handleLedgerPdf(current)}
          exportingPdf={exportingLedgerPdf}
        />
      ) : (
        <Card elevation={2} sx={{ borderRadius: 2, mb: 3, p: 2 }}>
          <Typography variant="subtitle1" fontWeight={700} mb={1}>
            Transactions
          </Typography>
          <Skeleton variant="rounded" height={120} />
        </Card>
      )}

      <MonthlyHistory
        year={historyYear}
        rows={history}
        loading={historyLoading}
        selected={isMonth ? { year: period.year, month: period.month } : null}
        busyKey={historyBusyKey}
        onYearChange={setHistoryYear}
        onOpenMonth={openMonth}
        onPdf={(y, m) => withHistoryReport(y, m, handleReportPdf)}
        onPrint={(y, m) => withHistoryReport(y, m, printReport)}
      />

      <TransactionDrilldownDialog
        projectId={projectId}
        period={period}
        periodLabel={current?.period.label ?? ""}
        target={drilldownTarget}
        breakdownTotal={drilldown?.amount ?? 0}
        onClose={() => setDrilldown(null)}
        onOpenFiles={setFiles}
      />
      <AttachmentViewer files={files} onClose={() => setFiles([])} />

      {/* Print-only copy of the report (hidden on screen; see utils/printStyles). */}
      {printable && <PrintableReport report={printable} />}
    </>
  );
};

export default BalanceSheet;
